'use client';

import { c as _c } from "react/compiler-runtime";
import type { InboxPullRequest, InboxStackContext, InboxSyncProgress, InboxV2Meta } from '@/app/(home)/inbox/types';
import { focusManager, useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { clearPendingOptimisticMutation, getActiveOptimisticSnapshots, isOptimisticSnapshotConfirmedByServer } from './pending-optimistic-mutations';
import { fetchInboxV2 } from '@/data/pull-requests/fetch-inbox-v2';
import { inboxV2MergedPullRequestsQueryKey, inboxV2OpenPullRequestsQueryKey } from '@/data/pull-requests/query-keys';
import { useSelectedRepositories } from '@/lib/hooks/use-selected-repositories';
type InboxV2PullRequestsOptions = {
  enabled?: boolean;
  staleTime?: number;
  refetchOnMount?: boolean | 'always';
  refetchOnWindowFocus?: boolean;
};
type InboxV2Section = 'open' | 'merged';
export type InboxV2SectionResponse = {
  openPullRequests: InboxPullRequest[];
  mergedPullRequests: InboxPullRequest[];
  stackContexts: InboxStackContext[];
  meta?: InboxV2Meta;
  installationId?: number | null;
};
type InboxV2SectionQueryResult = {
  pullRequests: InboxPullRequest[];
  stackContexts: InboxStackContext[];
  meta?: InboxV2Meta;
  installationId?: number | null;
};
const DEFAULT_INBOX_V2_STALE_TIME_MS = 30_000;
// Matches the inbox's realtime invalidation cooldown, so polling refreshes no faster than PR events.
const SYNC_POLL_INTERVAL_MS = 5_000;
// The open section re-checks its due pull requests with GitHub only when it reads (`refresh-policy.ts`
// in the inbox API), and a re-check that changes nothing sends no update to prompt a read. This poll
// keeps an open inbox re-checking, in background tabs too: CI from other apps, and the desktop alerts
// that follow it, reach the mirror only through those re-checks. A read the cache can serve is cheap.
const OPEN_SECTION_POLL_INTERVAL_MS = 60_000;
// Hidden tabs and desktop windows keep re-checking at a fifth of the pace, and pause the import poll.
const HIDDEN_OPEN_SECTION_POLL_INTERVAL_MS = 300_000;

/**
 * Import progress, read only from a response fetched since this page loaded: the saved query cache
 * restores an earlier session's meta, which can still list repositories that finished importing.
 */
function syncFetchedThisPage(data: {
  meta?: InboxV2Meta;
} | undefined, dataUpdatedAt: number): InboxSyncProgress | null {
  return dataUpdatedAt >= performance.timeOrigin ? data?.meta?.sync ?? null : null;
}

/**
 * Guards a server-fetched PR list against stale data by checking the optimistic lock registry.
 * When a pending optimistic snapshot exists for a PR (set during any mutation), the snapshot is
 * returned instead of the server version. The lock auto-expires after a short TTL.
 *
 * `clearConfirmed` writes to localStorage, so it must only run inside the `queryFn` (which executes
 * once per fetch). `select` runs during React's render phase and must stay pure — clearing there
 * would re-confirm the already-optimistic cached entry against itself and drop the lock before the
 * server has actually caught up.
 */
function preserveOptimisticEntries(serverList: InboxPullRequest[], {
  clearConfirmed = false
}: {
  clearConfirmed?: boolean;
} = {}): InboxPullRequest[] {
  const snapshots = getActiveOptimisticSnapshots();
  if (snapshots.size === 0) {
    return serverList;
  }
  return serverList.map(serverPr => {
    const key = `${serverPr.repository.owner.toLowerCase()}/${serverPr.repository.name.toLowerCase()}#${serverPr.number}`;
    const optimisticSnapshot = snapshots.get(key);
    if (optimisticSnapshot) {
      if (isOptimisticSnapshotConfirmedByServer(serverPr, optimisticSnapshot)) {
        if (clearConfirmed) {
          clearPendingOptimisticMutation({
            owner: serverPr.repository.owner,
            repo: serverPr.repository.name,
            number: serverPr.number
          });
        }
        return serverPr;
      }
      return optimisticSnapshot;
    }
    return serverPr;
  });
}
type InboxV2SectionQueryKey = readonly [string, string, InboxV2Section, number | null | undefined, string | null];

/**
 * Query function for Inbox V2 pull request sections.
 * The route still returns the legacy `{ openPullRequests, mergedPullRequests }` envelope even when
 * only one section is requested, because that keeps auth/error handling and callers stable while
 * we split loading behavior at the cache and UI layers.
 */
async function getInboxV2PullRequestsSectionQueryFn({
  queryKey,
  signal
}: {
  queryKey: InboxV2SectionQueryKey;
  signal: AbortSignal;
}): Promise<InboxV2SectionResponse> {
  const [,, section, installationId] = queryKey;
  const data = await fetchInboxV2<Partial<InboxV2SectionResponse>>({
    path: '/inbox/api/pull-requests',
    installationId,
    query: {
      section
    },
    signal,
    description: `${section} pull requests`
  });
  return {
    installationId: installationId as number | null | undefined,
    meta: data.meta,
    // Defensively normalize both arrays so partial or error payloads do not poison the cache shape.
    openPullRequests: Array.isArray(data.openPullRequests) ? data.openPullRequests : [],
    mergedPullRequests: Array.isArray(data.mergedPullRequests) ? data.mergedPullRequests : [],
    stackContexts: Array.isArray(data.stackContexts) ? data.stackContexts : []
  };
}

/**
 * Open and merged use thin wrappers instead of an inline closure so other consumers
 * like realtime prefetch can reuse the exact same section-aware query functions.
 */
export function getInboxV2OpenPullRequestsQueryFn(args: {
  queryKey: readonly unknown[];
  signal: AbortSignal;
}) {
  return getInboxV2PullRequestsSectionQueryFn({
    queryKey: args.queryKey as InboxV2SectionQueryKey,
    signal: args.signal
  });
}
export function getInboxV2MergedPullRequestsQueryFn(args: {
  queryKey: readonly unknown[];
  signal: AbortSignal;
}) {
  return getInboxV2PullRequestsSectionQueryFn({
    queryKey: args.queryKey as InboxV2SectionQueryKey,
    signal: args.signal
  });
}

/**
 * Shared hook for DB-backed inbox sections.
 * Each caller gets a section-specific `pullRequests` array while the cache key still records the
 * installation and selected repository scope that produced it.
 */
function useInboxV2SectionPullRequests(section, options) {
  const $ = _c(35);
  const queryEnabled = options?.enabled ?? true;
  let t0;
  if ($[0] !== queryEnabled) {
    t0 = {
      enabled: queryEnabled
    };
    $[0] = queryEnabled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    isError: isRepositorySelectionError,
    isLoading: isRepositorySelectionLoading,
    selectedRepositoryIds,
    normalizedInstallationId
  } = useSelectedRepositories(t0);
  let t1;
  if ($[2] !== normalizedInstallationId || $[3] !== section || $[4] !== selectedRepositoryIds) {
    t1 = section === "open" ? inboxV2OpenPullRequestsQueryKey(normalizedInstallationId, selectedRepositoryIds) : inboxV2MergedPullRequestsQueryKey(normalizedInstallationId, selectedRepositoryIds);
    $[2] = normalizedInstallationId;
    $[3] = section;
    $[4] = selectedRepositoryIds;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const queryKey = t1;
  const baseQueryFn = section === "open" ? getInboxV2OpenPullRequestsQueryFn : getInboxV2MergedPullRequestsQueryFn;
  const pageVisible = useSyncExternalStore(focusManager.subscribe, _temp, _temp2);
  let t2;
  if ($[6] !== baseQueryFn) {
    t2 = async args => {
      const serverData = await baseQueryFn(args);
      return {
        ...serverData,
        openPullRequests: preserveOptimisticEntries(serverData.openPullRequests, {
          clearConfirmed: true
        }),
        mergedPullRequests: preserveOptimisticEntries(serverData.mergedPullRequests, {
          clearConfirmed: true
        })
      };
    };
    $[6] = baseQueryFn;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = queryEnabled && selectedRepositoryIds.length > 0;
  const t4 = options?.staleTime ?? DEFAULT_INBOX_V2_STALE_TIME_MS;
  const t5 = options?.refetchOnWindowFocus ?? true;
  const t6 = options?.refetchOnMount ?? false;
  let t7;
  if ($[8] !== pageVisible || $[9] !== section) {
    t7 = query => {
      if (!pageVisible) {
        return section === "open" ? HIDDEN_OPEN_SECTION_POLL_INTERVAL_MS : false;
      }
      if (syncFetchedThisPage(query.state.data, query.state.dataUpdatedAt)) {
        return SYNC_POLL_INTERVAL_MS;
      }
      return section === "open" ? OPEN_SECTION_POLL_INTERVAL_MS : false;
    };
    $[8] = pageVisible;
    $[9] = section;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = section === "open";
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      persist: true
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== normalizedInstallationId || $[13] !== section || $[14] !== selectedRepositoryIds.length) {
    t10 = data => {
      if (selectedRepositoryIds.length === 0) {
        return {
          installationId: normalizedInstallationId,
          pullRequests: [],
          stackContexts: []
        };
      }
      const dataInstallationId = data.installationId === 0 ? null : data.installationId;
      const isMismatch = dataInstallationId !== undefined && normalizedInstallationId !== null && dataInstallationId !== null && dataInstallationId !== normalizedInstallationId;
      if (isMismatch) {
        return {
          installationId: normalizedInstallationId,
          meta: data.meta,
          pullRequests: [],
          stackContexts: []
        };
      }
      const sectionPrs = section === "open" ? data.openPullRequests : data.mergedPullRequests;
      return {
        installationId: data.installationId,
        meta: data.meta,
        pullRequests: preserveOptimisticEntries(sectionPrs),
        stackContexts: Array.isArray(data.stackContexts) ? data.stackContexts : []
      };
    };
    $[12] = normalizedInstallationId;
    $[13] = section;
    $[14] = selectedRepositoryIds.length;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== queryKey || $[17] !== t10 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t11 = {
      queryKey,
      queryFn: t2,
      enabled: t3,
      staleTime: t4,
      refetchOnWindowFocus: t5,
      refetchOnMount: t6,
      refetchOnReconnect: true,
      refetchInterval: t7,
      refetchIntervalInBackground: t8,
      meta: t9,
      select: t10
    };
    $[16] = queryKey;
    $[17] = t10;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const query_0 = useQuery(t11);
  let t12;
  if ($[26] !== query_0.data || $[27] !== query_0.dataUpdatedAt) {
    t12 = syncFetchedThisPage(query_0.data, query_0.dataUpdatedAt);
    $[26] = query_0.data;
    $[27] = query_0.dataUpdatedAt;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = selectedRepositoryIds.length > 0;
  const t14 = queryEnabled && isRepositorySelectionLoading;
  const t15 = queryEnabled && isRepositorySelectionError;
  let t16;
  if ($[29] !== query_0 || $[30] !== t12 || $[31] !== t13 || $[32] !== t14 || $[33] !== t15) {
    t16 = {
      ...query_0,
      sync: t12,
      hasSelectedRepositories: t13,
      isRepositoryScopeLoading: t14,
      isRepositoryScopeError: t15
    };
    $[29] = query_0;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}

/**
 * Open PRs are the first-content path for the DB inbox.
 */
function _temp2() {
  return true;
}
function _temp() {
  return focusManager.isFocused();
}
export function useInboxV2OpenPullRequests(options) {
  return useInboxV2SectionPullRequests("open", options);
}

/**
 * Merged PRs intentionally load independently so history can appear after the main review work.
 */
export function useInboxV2MergedPullRequests(options) {
  return useInboxV2SectionPullRequests("merged", options);
}